const DEFAULT_META = Object.freeze({priority: 0, suspendable: false});

class AudioManager {
  constructor() {
    this.channels = new Map();
    this.channelMeta = new Map();
  }

  /**
   * Register a channel's preemption metadata. Channels are still created
   * lazily by ensureChannel; defining them up-front only sets the rules
   * beginRequest uses to resolve cross-channel conflicts.
   *
   *   priority    — higher wins. A new request on channel X is blocked
   *                 (returns null) if any other channel with strictly
   *                 higher priority currently has audio loaded.
   *   suspendable — when X is preempted by an equal- or higher-priority
   *                 request on a different channel, it is paused (audio
   *                 element + currentTime preserved so a later resume()
   *                 picks up where it left off). When false, the displaced
   *                 channel is fully stopped/cleared.
   */
  defineChannel(channelName, options = {}) {
    if (!channelName) return;
    const previous = this.channelMeta.get(channelName) || {};
    this.channelMeta.set(channelName, {
      priority: Number.isFinite(options.priority)
        ? options.priority
        : previous.priority ?? DEFAULT_META.priority,
      suspendable:
        typeof options.suspendable === 'boolean'
          ? options.suspendable
          : previous.suspendable ?? DEFAULT_META.suspendable,
    });
  }

  getChannelMeta(channelName) {
    return this.channelMeta.get(channelName) || DEFAULT_META;
  }

  ensureChannel(channelName) {
    const channel = channelName || 'default';
    if (!this.channels.has(channel)) {
      this.channels.set(channel, {
        requestToken: 0,
        audio: null,
        audioKey: null,
        stopCallback: null,
        finishPlayback: null,
        startProgressUpdates: null,
        stopProgressUpdates: null,
      });
    }
    return this.channels.get(channel);
  }

  clearChannelState(channelName) {
    const state = this.ensureChannel(channelName);
    state.audio = null;
    state.audioKey = null;
    state.stopCallback = null;
    state.finishPlayback = null;
    state.startProgressUpdates = null;
    state.stopProgressUpdates = null;
  }

  /**
   * Returns the name of any other channel currently holding audio that
   * outranks `channelName`, or null if `channelName` is free to play.
   */
  findBlockingChannel(channelName) {
    const myMeta = this.getChannelMeta(channelName);
    for (const [otherName, otherState] of this.channels) {
      if (otherName === channelName) continue;
      if (!otherState.audio) continue;
      const otherMeta = this.getChannelMeta(otherName);
      if (otherMeta.priority > myMeta.priority) {
        return otherName;
      }
    }
    return null;
  }

  /**
   * Begin a new playback request on `channelName`. Enforces, in order:
   *   1. If a higher-priority channel currently owns audio, the request
   *      is rejected (returns null) — the caller must abort. The
   *      higher-priority audio is left untouched.
   *   2. Otherwise, every other channel with active audio is preempted:
   *      suspendable channels are paused (currentTime preserved), the
   *      rest are fully stopped.
   *   3. Any prior playback on `channelName` itself is stopped (existing
   *      behavior).
   */
  beginRequest(channelName) {
    const blockingChannel = this.findBlockingChannel(channelName);
    if (blockingChannel) {
      return null;
    }

    for (const [otherName, otherState] of this.channels) {
      if (otherName === channelName) continue;
      if (!otherState.audio) continue;
      const otherMeta = this.getChannelMeta(otherName);
      if (otherMeta.suspendable) {
        this.pause(otherName);
      } else {
        this.stop(otherName, {triggerStop: true, invalidate: true});
      }
    }

    const state = this.ensureChannel(channelName);
    state.requestToken += 1;
    this.stop(channelName, {triggerStop: true, invalidate: false});
    return state.requestToken;
  }

  isRequestActive(channelName, requestToken) {
    const state = this.ensureChannel(channelName);
    return state.requestToken === requestToken;
  }

  registerPlayback(channelName, requestToken, playbackState) {
    const state = this.ensureChannel(channelName);
    if (state.requestToken !== requestToken) {
      playbackState?.stopProgressUpdates?.();
      if (playbackState?.audio) {
        playbackState.audio.pause();
        playbackState.audio.currentTime = 0;
      }
      return false;
    }

    state.audio = playbackState.audio || null;
    state.audioKey = playbackState.audioKey || null;
    state.stopCallback = playbackState.onStop || null;
    state.finishPlayback = playbackState.onFinish || null;
    state.startProgressUpdates = playbackState.startProgressUpdates || null;
    state.stopProgressUpdates = playbackState.stopProgressUpdates || null;
    return true;
  }

  clearPlaybackIfMatches(channelName, audioRef) {
    const state = this.ensureChannel(channelName);
    if (audioRef && state.audio !== audioRef) return;
    this.clearChannelState(channelName);
  }

  stop(channelName, options = {}) {
    const { triggerStop = true, invalidate = true } = options;
    const state = this.ensureChannel(channelName);
    if (invalidate) {
      state.requestToken += 1;
    }

    if (!state.audio) return;

    const audio = state.audio;
    const stopCallback = state.stopCallback;
    const finishPlayback = state.finishPlayback;
    state.stopProgressUpdates?.();
    audio.onended = null;
    audio.onerror = null;
    audio.pause();
    audio.currentTime = 0;
    this.clearChannelState(channelName);
    if (triggerStop) {
      stopCallback?.();
    }
    finishPlayback?.();
  }

  pause(channelName) {
    const state = this.ensureChannel(channelName);
    if (!state.audio) return false;
    state.stopProgressUpdates?.();
    state.audio.pause();
    return true;
  }

  async resume(channelName) {
    const state = this.ensureChannel(channelName);
    if (!state.audio) return false;
    await state.audio.play();
    state.startProgressUpdates?.();
    return true;
  }

  getCurrentAudioKey(channelName) {
    const state = this.ensureChannel(channelName);
    return state.audioKey;
  }

  getCurrentAudio(channelName) {
    const state = this.ensureChannel(channelName);
    return state.audio;
  }
}

const audioManager = new AudioManager();

export default audioManager;
